import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Youtube } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import CriticalPinDialog from "../CriticalPinDialog";

export default function VideosTab() {
    const [videos, setVideos] = useState(null);
    const [url, setUrl] = useState("");
    const [title, setTitle] = useState("");
    const [busy, setBusy] = useState(false);
    const [pinTarget, setPinTarget] = useState(null);

    const load = () => api.get("/videos").then((r) => setVideos(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const add = async () => {
        if (!url.trim()) return toast.error("YouTube bağlantısı zorunludur");
        setBusy(true);
        try {
            await api.post("/admin/videos", { url: url.trim(), title: title.trim() });
            toast.success("Video eklendi — ana sayfada yayında");
            setUrl("");
            setTitle("");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Video eklenemedi");
        } finally {
            setBusy(false);
        }
    };

    const del = async (pin) => {
        try {
            await api.delete(`/admin/records/videos/${pinTarget.video_id}`, { headers: { "X-Critical-Pin": pin } });
            toast.success("Video kaldırıldı");
            setPinTarget(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Silinemedi");
        }
    };

    if (!videos) return <div className="space-y-3">{[...Array(2)].map((_, i) => <div key={i} className="h-24 animate-pulse rounded-2xl bg-muted" />)}</div>;

    return (
        <div className="space-y-6" data-testid="videos-tab">
            <p className="text-sm text-muted-foreground">
                Ana sayfadaki "Videolar" bölümünde gösterilecek YouTube videolarını buradan yönetin. Video eklenmezse bölüm ana sayfada gizlenir.
            </p>

            <div className="rounded-2xl border border-dashed bg-card p-5" data-testid="video-add-form">
                <p className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <Youtube className="h-4 w-4 text-terra-600" /> Yeni Video Ekle
                </p>
                <div className="grid gap-2 sm:grid-cols-[1fr_240px]">
                    <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.youtube.com/watch?v=... veya https://youtu.be/..." data-testid="video-url-input" />
                    <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Başlık (opsiyonel)" data-testid="video-title-input" />
                </div>
                <Button onClick={add} disabled={busy} className="mt-3 bg-terra-600 text-white hover:bg-terra-700" data-testid="video-add-button">
                    {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />} Videoyu Ekle
                </Button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {videos.map((v) => (
                    <div key={v.video_id} className="overflow-hidden rounded-2xl border bg-card shadow-sm" data-testid={`video-row-${v.video_id}`}>
                        <div className="relative aspect-video bg-stone-950">
                            <img src={`https://i.ytimg.com/vi/${v.youtube_id}/hqdefault.jpg`} alt={v.title} className="h-full w-full object-cover" loading="lazy" />
                        </div>
                        <div className="flex items-center gap-2 p-3">
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-semibold">{v.title}</p>
                                <p className="truncate text-xs text-muted-foreground">{v.url}</p>
                            </div>
                            <Button size="sm" variant="outline" onClick={() => setPinTarget(v)} className="shrink-0 text-destructive hover:text-destructive" data-testid={`video-delete-${v.video_id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    </div>
                ))}
                {videos.length === 0 && <p className="text-sm text-muted-foreground">Henüz video eklenmemiş.</p>}
            </div>

            <CriticalPinDialog
                open={!!pinTarget}
                onClose={() => setPinTarget(null)}
                onSubmit={del}
                actionLabel={`Video kaldırma: ${pinTarget?.title || ""}`}
            />
        </div>
    );
}
